import { createMiddleware } from "@tanstack/react-start";
import { isRedirect, isNotFound } from "@tanstack/react-router";
import * as Sentry from "@sentry/cloudflare";

/**
 * Global server-function middleware: forwards uncaught handler errors to
 * Sentry (initialized by `withSentry` in src/server.ts), then rethrows so
 * TanStack's normal RPC error response is still returned to the caller.
 *
 * Expected control-flow "errors" (redirect / notFound / auth 401s) are
 * ignored to avoid alert noise.
 */
export const sentryFunctionMiddleware = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    try {
      return await next();
    } catch (error) {
      if (isIgnorableError(error)) throw error;
      try {
        Sentry.withScope((scope) => {
          scope.setTag("source", "serverFn");
          Sentry.captureException(error);
        });
      } catch {
        /* never let Sentry itself break the request */
      }
      throw error;
    }
  },
);

function isIgnorableError(err: unknown): boolean {
  if (err instanceof Response) return true;
  if (isRedirect(err) || isNotFound(err)) return true;

  if (err && typeof err === "object") {
    const anyErr = err as { statusCode?: unknown; message?: unknown };
    if (typeof anyErr.statusCode === "number" && anyErr.statusCode >= 400 && anyErr.statusCode < 500) {
      return true;
    }
    if (typeof anyErr.message === "string" && anyErr.message.startsWith("Unauthorized")) {
      return true;
    }
  }
  return false;
}
